import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import config from "../config";

const VerifyEmail = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const queryParams = new URLSearchParams(location.search);
  const token = queryParams.get("token");
  const [message, setMessage] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [isSuccess, setIsSuccess] = useState(false);

  const handleLogin = () => {
    navigate("/login");
  };

  useEffect(() => {
    const verifyEmail = async () => {
      if (!token) {
        setMessage("Invalid verification link.");
        setIsLoading(false);
        return;
      }

      try {
        const response = await axios.get(
          `${config.API_BASE_URL}/auth/verify-email?token=${token}`
        );
        if (response.data.status) {
          setMessage("Email verified successfully. You can now login.");
          setIsSuccess(true);
        } else {
          setMessage("Email verification failed. Please try again.");
        }
      } catch (error) {
        console.error("Error verifying email", error);
        if (error.response && error.response.status === 400) {
          setMessage(
            "Invalid or expired verification link. Please request a new verification email."
          );
        } else {
          setMessage("Error verifying email. Please try again.");
        }
      } finally {
        setIsLoading(false);
      }
    };

    verifyEmail();
  }, [token]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full">
        {/* Header Section */}
        <div className="text-center mb-8">
          <div className="bg-white rounded-full p-3 shadow-lg inline-flex items-center justify-center mb-4">
            <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-purple-600 rounded-full flex items-center justify-center">
              <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
            </div>
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            Email Verification
          </h1>
          <p className="text-gray-600">
            We're verifying your email address
          </p>
        </div>

        {/* Main Card */}
        <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
          <div className="p-8 text-center">
            {isLoading ? (
              <div className="flex flex-col items-center">
                <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-blue-500 mb-6"></div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  Verifying Your Email
                </h3>
                <p className="text-gray-600 text-base leading-relaxed">
                  Please wait while we verify your email address...
                </p>
              </div>
            ) : (
              <>
                {isSuccess ? (
                  <>
                    <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                      <svg className="w-10 h-10 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <h3 className="text-2xl font-bold text-gray-900 mb-4">
                      Email Verified Successfully!
                    </h3>
                    <p className="text-gray-600 text-base leading-relaxed mb-2">
                      Your email has been verified successfully.
                    </p>
                    <p className="text-gray-600 text-base mb-6">
                      You can now login to your account.
                    </p>
                    <button
                      onClick={handleLogin}
                      className="w-full bg-gradient-to-r from-blue-600 to-purple-600 text-white py-4 px-6 rounded-lg font-semibold hover:from-blue-700 hover:to-purple-700 hover:shadow-lg transform hover:scale-105 transition-all duration-300 focus:outline-none focus:ring-4 focus:ring-blue-300 focus:ring-opacity-50"
                    >
                      Continue to Login
                    </button>
                  </>
                ) : (
                  <>
                    <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
                      <svg className="w-10 h-10 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </div>
                    <h3 className="text-2xl font-bold text-gray-900 mb-4">
                      Verification Failed
                    </h3>
                    <p className="text-gray-600 text-base leading-relaxed mb-6">
                      {message}
                    </p>
                    <button
                      onClick={() => navigate("/register")}
                      className="w-full bg-gradient-to-r from-blue-600 to-purple-600 text-white py-4 px-6 rounded-lg font-semibold hover:from-blue-700 hover:to-purple-700 hover:shadow-lg transform hover:scale-105 transition-all duration-300 focus:outline-none focus:ring-4 focus:ring-blue-300 focus:ring-opacity-50"
                    >
                      Back to Registration
                    </button>
                  </>
                )}
              </>
            )}
          </div>
        </div>

        {/* Support Link */}
        <div className="text-center mt-6">
          <p className="text-gray-600 text-sm">
            Need help?{" "}
            <button
              onClick={() => navigate("/contact")}
              className="text-blue-600 hover:text-blue-800 font-medium transition-colors duration-200"
            >
              Contact Support
            </button>
          </p>
        </div>
      </div>
    </div>
  );
};

export default VerifyEmail;